import * as React from 'react';
import client from '../apollo/client';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer, Slide } from 'react-toastify';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Portal from '@mui/material/Portal';
import { useReactiveVar } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import * as Device from 'react-device-detect';

import {
  EntityDialog as AccountEventDialog,
  BookingDialog,
  BookingCreateDialog,
  AuthenticationDialog,
  OAuthRegisterDialog,
  GreetingDialog,
  Meta,
  ConsentBanner,
  startSentryIfNeeded,
  ConsentState,
} from '@components';

import {
  usePreventClickOnScroll,
  useStatusBarScrollToTop,
  usePushNotifications,
  useGeolocation,
  useAppDeepLink,
  useSubscriptions,
  useElementHeight,
  useLogroutes,
} from '@hooks';
import { AppBar } from './app-bar';
import { BottomNavNative } from './BottomNavNative';
import AppLoader from './AppLoader';
import { BackendOfflineScreen } from './BackendOfflineScreen';
import { networkStatusVar } from '../context';

export default function Layout() {
  const location = useLocation();
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  // Handle iOS status bar tap to scroll to top
  useStatusBarScrollToTop(scrollRef);
  usePreventClickOnScroll<HTMLDivElement>(scrollRef, 350);
  usePushNotifications({ initialize: true });
  useSubscriptions();
  useGeolocation();
  useAppDeepLink();
  useLogroutes();
  const { isBackendUnreachable, retryCount } = useReactiveVar(networkStatusVar);

  // Handle user consent for monitoring
  const handleConsentResolved = React.useCallback(async (state: ConsentState) => {
    if (state.monitoring === true) {
      await startSentryIfNeeded();
    }
  }, []);

  const appbarHeight = useElementHeight({ selector: '#appbar' });
  const bottomNavHeight = useElementHeight({ selector: '#bottomnav' });

  const scrollSx = {
    position: 'relative',
    overflowY: 'auto',
    '& > *': {
      scrollbarWidth: 'none', // hide scrollbar Firefox
      msOverflowStyle: 'none', // hide scrollbar IE and Edge
    },
    '&::-webkit-scrollbar': {
      display: 'none', // hide scrollbar Chrome, Safari
    },
    pt: `${appbarHeight}px`,
    pb: location.pathname.startsWith('/me/calendar') ? undefined : `${bottomNavHeight}px`,
    ...(Capacitor.isNativePlatform() && {
      pt: `${appbarHeight}px`, // safe area handled by global theme's appbar
      pr: `env(safe-area-inset-right)`,
      pb: location.pathname.startsWith('/me/calendar') ? undefined : `${bottomNavHeight}px`,
      pl: `env(safe-area-inset-left)`,
    }),
  };

  return (
    <>
      <CssBaseline />
      <ScrollRestoration getKey={() => location.pathname} />
      <AppBar scrollRef={scrollRef} />
      <Box ref={scrollRef} component="main" width="100vw" height="100dvh" sx={scrollSx}>
        {isBackendUnreachable ? (
          <BackendOfflineScreen
            isBackendUnreachable={isBackendUnreachable}
            retryCount={retryCount}
            onRetry={() => {
              client.refetchQueries({ include: 'active' });
            }}
          />
        ) : (
          <>
            <Outlet />
            <AppLoader />
            <GreetingDialog />
            <AuthenticationDialog />
            <OAuthRegisterDialog />
            <BookingDialog />
            <BookingCreateDialog />
            <AccountEventDialog />
            <Meta />
            {Device.isMobile ? <BottomNavNative /> : null}
          </>
        )}
      </Box>
      <ConsentBanner onConsentResolved={handleConsentResolved} />
      <Portal>
        <ToastContainer
          style={{
            ...(import.meta.env.MODE === 'development' && { width: '500px' }),
            marginBottom: Device.isDesktop ? undefined : 'calc(env(safe-area-inset-bottom) + 45px)',
            fontSize: Device.isDesktop
              ? undefined // let toast use default font size on desktop
              : 12,
          }}
          toastStyle={{
            whiteSpace: 'normal',
            wordBreak: 'break-all',
          }}
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={true}
          newestOnTop={false}
          closeButton={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          pauseOnHover={false}
          theme="dark"
          transition={Slide}
        />
      </Portal>
    </>
  );
}
